import { setBackgroundInert } from './modal-background-inert.js';
import { createModalFocusScope } from './modal-focus-scope.js';

const STORAGE_KEY = 'casualer.guestTermsAcceptedVersion';
const ACCEPTED_EVENT = 'guest-terms-consent:accepted';

/**
 * Runs `callback` now, or once the open consent modal is accepted. A page module that opens its own
 * modal on load goes through this: opened under the consent, it would sit inside the inert page while
 * taking the top of the focus stack, and Tab could no longer reach `I agree`.
 */
export const whenGuestTermsConsentSettled = (callback, documentRef = document) => {
    const modal = documentRef.querySelector('[data-guest-terms-consent]');
    if (!(modal instanceof HTMLElement) || modal.hidden) {
        callback();

        return;
    }

    documentRef.addEventListener(ACCEPTED_EVENT, () => callback(), { once: true });
};

const readAcceptedVersion = (windowRef) => {
    try {
        return windowRef.localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
};

const storeAcceptedVersion = (windowRef, version) => {
    try {
        windowRef.localStorage.setItem(STORAGE_KEY, version);
    } catch {
        // Without storage the modal returns on the next page; accepting still closes it for this one.
    }
};

export const initGuestTermsConsent = (
    documentRef = document,
    windowRef = window,
) => {
    const modal = documentRef.querySelector('[data-guest-terms-consent]');
    if (!(modal instanceof HTMLElement)) {
        return false;
    }

    const version = String(modal.dataset.termsVersion ?? '').trim();
    const dialog = modal.querySelector('[data-guest-terms-consent-dialog]');
    const acceptButton = modal.querySelector('[data-guest-terms-consent-accept]');
    if (
        version === ''
        || readAcceptedVersion(windowRef) === version
        || !(dialog instanceof HTMLElement)
        || !(acceptButton instanceof HTMLElement)
    ) {
        return false;
    }

    // Accepting or leaving through `Back to home` are the only ways out: Escape is deliberately ignored.
    const focusScope = createModalFocusScope({
        dialog,
        requestClose: () => {},
        documentRef,
    });
    if (focusScope === null) {
        return false;
    }

    acceptButton.addEventListener('click', () => {
        storeAcceptedVersion(windowRef, version);
        modal.hidden = true;
        setBackgroundInert(modal, false, documentRef);
        focusScope.deactivate();
        documentRef.dispatchEvent(new CustomEvent(ACCEPTED_EVENT));
    });

    modal.hidden = false;
    focusScope.activate(null);
    // The dialog, not the button, takes focus first: the message is read before agreeing is one key away.
    focusScope.focus(dialog);
    setBackgroundInert(modal, true, documentRef);

    return true;
};
